import { Box, Button, IconButton, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { RestaurantCardImgSectionCircularProgress } from '../../../../components/styled/restaurant/RestaurantCard.styled'
import { generateAddress } from '../../../../helpers/converters'
import { DEFAULT_CARD_IMAGE } from '../../../../helpers/static-constants'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import { selectSavedRestaurantIds } from '../../../../store/profileSlice'
import { favoriteRestaurant } from '../../../../store/restaurantSlice'
import { Restaurant } from '../../../../types'
import { IReservation } from '../../../../types/reservationTypes'
import { DrawerHeader } from '../../group/styled/ChatDrawer.styled'
import RestaurantShare from '../../restaurant/components/RestaurantShare'
import {
  RestaurantBasicInfoAddressTypography,
  RestaurantBasicInfoDistanceTypography,
  RestaurantBasicInfoIconMapPin,
  RestaurantBasicInfoIconPointFilled,
  RestaurantInfoActionDirectionsWebButtonIconArrowBearRight,
  RestaurantInfoActionIconBookmark,
  RestaurantInfoActionIconBookmarkFilled,
  RestaurantInfoActionIconMessage,
  RestaurantInfoActionIconPhone,
} from '../../restaurant/styled/RestaurantInfo.styled'
import CloseButton from '../../../../components/ui/button/CloseButton'

interface ReservationDetailProps {
  reservation: IReservation
  onClose: () => void
  openChat: () => void
  onCancel: () => void
}

const ReservationDetail: React.FC<ReservationDetailProps> = ({
  reservation,
  onClose,
  openChat,
  onCancel,
}) => {
  const { theme, dispatch } = useAppUtilities()
  const saveRestaurants = useAppSelector(selectSavedRestaurantIds)
  const [isSaved, setIsSaved] = useState(false)
  const [staticLoader, setStaticLoader] = useState(false)
  const [mapUrl, setMapUrl] = useState<string>('')

  const addressParts = [
    reservation.restaurant?.address1,
    reservation.restaurant?.city,
    reservation.restaurant?.state,
  ]
  const address = addressParts.filter(Boolean).join(', ')

  useEffect(() => {
    if (saveRestaurants?.length) {
      const isSaved = saveRestaurants.includes(
        reservation.restaurant?.restaurantId ?? '',
      )
      setIsSaved(isSaved)
    }
  }, [saveRestaurants])

  useEffect(() => {
    const fetchMapUrl = async (): Promise<void> => {
      const url = await onMapDirections(reservation?.restaurant)
      setMapUrl(url ?? '')
    }
    void fetchMapUrl()
  }, [reservation?.restaurant?.restaurantId])

  const onClickBookmark = async (): Promise<void> => {
    setStaticLoader(true)
    await dispatch(favoriteRestaurant(reservation.restaurant?.alias ?? ''))
    setStaticLoader(false)
  }

  const onMapDirections = async (restaurant: Restaurant): Promise<string> => {
    if (!restaurant) {
      return 'https://www.google.com/maps/dir/?api=1&destination='
    }

    if (restaurant.placeId) {
      return `https://www.google.com/maps/search/?api=1&query=%20&query_place_id=${restaurant.placeId}`
    }

    return `https://www.google.com/maps/place/${generateAddress(restaurant)}`
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'grey.300',
        }}
      >
        <DrawerHeader>
          <Typography variant="body2">Reservation Details</Typography>

          <CloseButton color="bordered" size="small" onClick={onClose} />
        </DrawerHeader>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        <Box
          component="img"
          src={reservation.restaurant?.imageUrl ?? DEFAULT_CARD_IMAGE}
          sx={{
            border: 1,
            borderColor: 'grey.200',
            borderRadius: '16px',
            boxShadow: '0px 0px 2px 0px #1A1A1A40',
            width: '100%',
            aspectRatio: 5 / 2.9,
            objectFit: 'cover',
          }}
        />

        <Typography variant="h4" mt={2} maxWidth={'100%'} noWrap>
          {reservation.restaurant.name}
        </Typography>

        <Box display={'flex'} alignItems={'center'} mt={0.5}>
          <RestaurantBasicInfoIconMapPin />
          <RestaurantBasicInfoAddressTypography>
            {address}
          </RestaurantBasicInfoAddressTypography>
          {reservation.restaurant && reservation.restaurant.distance !== 0 && (
            <>
              <RestaurantBasicInfoIconPointFilled />
              <RestaurantBasicInfoDistanceTypography>
                {reservation.restaurant.distance} mi
              </RestaurantBasicInfoDistanceTypography>
            </>
          )}
        </Box>
        <Box
          mt={2}
          width="100%"
          sx={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}
        >
          <Button
            component="a"
            variant="outlined"
            color="primary"
            size="small"
            target="_blank"
            href={mapUrl}
            startIcon={
              <RestaurantInfoActionDirectionsWebButtonIconArrowBearRight />
            }
            disableElevation={true}
            rel="noopener noreferrer"
            sx={{
              fontSize: '12px',
              flex: 1,
              borderRadius: '10px',
              textTransform: 'capitalize',
              borderColor: theme.palette.grey[300],
              '&.MuiButton-root': {
                borderColor: theme.palette.grey[300],
              },
            }}
          >
            Directions
          </Button>

          {reservation.restaurant?.phone && (
            <Button
              component="a"
              href={`tel:${reservation.restaurant?.phone}`}
              variant="outlined"
              color="info"
              sx={{
                borderRadius: '50%',
                height: '36px',
                width: '36px',
                minWidth: '36px',
                p: 0,
              }}
            >
              <RestaurantInfoActionIconPhone />
            </Button>
          )}
          <IconButton color="bordered" onClick={openChat}>
            <RestaurantInfoActionIconMessage />
          </IconButton>
          <IconButton
            color="bordered"
            onClick={() => {
              void onClickBookmark()
            }}
          >
            {staticLoader ? (
              <RestaurantCardImgSectionCircularProgress />
            ) : isSaved ? (
              <RestaurantInfoActionIconBookmarkFilled />
            ) : (
              <RestaurantInfoActionIconBookmark />
            )}
          </IconButton>

          <RestaurantShare
            restaurantId={reservation.restaurant?.restaurantId ?? ''}
            restaurantHandle={reservation.restaurant?.instagram ? `@${reservation.restaurant.instagram.replace(/^@/, '')}` : null}
            imgUrl={reservation.restaurant?.imageUrl ?? ''}
            description={reservation.restaurant?.description ?? ''}
            title={reservation.restaurant?.name ?? ''}
            cuisine={reservation.restaurant?.cuisine?.replace(/,/g, ' ') ?? ''}
            city={reservation.restaurant?.city ?? ''}
            url={`restaurant/${reservation.restaurant?.alias}/details`}
          />
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          p={2}
          bgcolor="grey.100"
          borderRadius="12px"
          my={3}
        >
          <Box>
            <Typography variant="body2" color="grey.600" fontWeight={500}>
              Reservation Date
            </Typography>
            <Typography mb={2} variant="body2">
              {reservation?.reservationDate && reservation?.time
                ? dayjs(
                    `${reservation?.reservationDate} ${reservation?.time}`,
                  ).format('D MMMM YYYY, h:mm A')
                : '-'}
            </Typography>
            <Typography variant="body2" color="grey.600" fontWeight={500}>
              Total Person
            </Typography>
            <Typography variant="body2">{reservation.guests}</Typography>
          </Box>
          <Typography
            variant="body2"
            component="a"
            color="error.main"
            alignSelf="flex-start"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={onCancel}
          >
            Cancel Booking
          </Typography>
        </Box>

        <Box
          width="100%"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { md: 0 },
            height: { xs: 250, md: 'none' },
          }}
        >
          <Typography sx={{ flexShrink: 0 }} variant="subtitle1">
            Terms & Conditions
          </Typography>

          <Box
            component="ul"
            sx={{
              pl: 3,
              color: 'grey.600',
              mt: 1,
              overflowY: 'auto',
              flexGrow: 1,
              minHeight: 0,
            }}
          >
            {termsAndConditions?.map((item: string, index) => (
              <Box component="li" key={index}>
                <Typography
                  variant="subtitle1"
                  component="span"
                  color="grey.600"
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: 'grey.300',
          alignSelf: 'flex-end',
          width: '100%',
        }}
      >
        <Button fullWidth variant="contained">
          Notify Me
        </Button>
      </Box>
    </Box>
  )
}

export default ReservationDetail

const termsAndConditions = [
  'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate cras amet massa. Diam donec in ut tortor nisi.',
  'Blandit ornare. Molestie habitant facilisi dui nunc mattis tellus odio nec non.',
  'Lorem ipsum dolor sit amet consectetur.',
  'Lorem ipsum dolor sit amet consectetur. Suspendisse luctus porta morbi vulputate crastis tellus odio nec non.',
]
